import { Color } from '../svg/svg';
import { samples, TransferCharacteristic, transferCharacteristic } from './transfer-characteristic';
import { Scaler } from "./util";

export interface ColorMapEntry {
	value: number,
	color: Color
}

export const colorToString = (color: Color, alpha = true): string => alpha ? `hsla(${color.hue}, ${color.saturation}%, ${color.lightness}%, ${color.alpha} )` : `hsl(${color.hue}, ${color.saturation}%, ${color.lightness}% )`;

/**
* A function that maps values on the domain to a Color. Values before the start of the domain are clamped 
* to the start value. Values after the end of the domain are clamped to the end value. When the domainRange is 
* positve then the domain start is lower then the domain end. When the domainRange is negative then the domain 
* start is higher then the domain start. The domainValues, hueTransfer, saturationTransfer, lightnessTransfer and 
* alphaTransfer were used during the construction of the ColorInterpolator . The domain, domainRange and
* domainValues are taken from the hue transfer characteristic. Otherwise these are taken from an optional alternative
*  domain transformation characteristic.
 */
export type ColorInterpolator = { (value: number): Color, domain?: [number, number], domainRange?: number, domainValues?: number[], hueTransfer?: TransferCharacteristic, saturationTransfer?: TransferCharacteristic, lightnessTransfer?: TransferCharacteristic, alphaTransfer?: TransferCharacteristic };

const TRANSPARENT_COLOR: Color = { hue: 0, saturation: 0, lightness: 0, alpha: 0 };

export const paletteEntries: ColorMapEntry[] = [
	{ value: 0, color: { hue: 0, saturation: 99.0, lightness: 35.8, alpha: 1.0 } },
	{ value: 7, color: { hue: 7, saturation: 100.0, lightness: 36.0, alpha: 1.0 } },
	{ value: 22, color: { hue: 22, saturation: 90.0, lightness: 45.0, alpha: 1.0 } },
	{ value: 45, color: { hue: 45, saturation: 98.0, lightness: 45.0, alpha: 1.0 } },
	{ value: 115, color: { hue: 115, saturation: 98.0, lightness: 30.0, alpha: 1.0 } },
	{ value: 187, color: { hue: 187, saturation: 95.0, lightness: 38.0, alpha: 1.0 } },
	{ value: 208, color: { hue: 208, saturation: 100.0, lightness: 36.0, alpha: 1.0 } },
	{ value: 296, color: { hue: 296, saturation: 90.0, lightness: 34.0, alpha: 1.0 } },
	{ value: 360, color: { hue: 360, saturation: 99.0, lightness: 35.8, alpha: 1.0 } }];

const NOMINAL_MONO_HUE_SATURATION_PROFILE = [0.0, 0.75, 0.52, 0.46, 0.59, 0.70, 0.77, 0.76, 1.0, 1.0];
const NOMINAL_MONO_HUE_LIGHTNESS_PROFILE = [1.0, 0.95, 0.86, 0.73, 0.66, 0.57, 0.46, 0.38, 0.25, 0.20];

const MONO_HUE_SATURATION_PROFILE = transferCharacteristic(samples([0, 1], 10), NOMINAL_MONO_HUE_SATURATION_PROFILE, 100.0);
const MONO_HUE_LIGHTNESS_PROFILE = transferCharacteristic(samples([0, 1], 10), NOMINAL_MONO_HUE_LIGHTNESS_PROFILE, 100.0);

const profile = (prfl: TransferCharacteristic | [number, number], range = [0, 1]): TransferCharacteristic => {
	return prfl ? (<number[]>prfl).lastIndexOf ? transferCharacteristic(<number[]>prfl, range) : <TransferCharacteristic>prfl : undefined;
}

export const getColorInterpolator = (hue: TransferCharacteristic, saturation?: TransferCharacteristic, lightness?: TransferCharacteristic, alpha?: TransferCharacteristic): ColorInterpolator => {
	const h = hue;
	const s = saturation ? saturation : transferCharacteristic([0], [100.0]);
	const l = lightness ? lightness : transferCharacteristic([0], [100.0]);
	const a = alpha ? alpha : transferCharacteristic([0], [1.0]);

	return Object.assign(
		(domainValue: number) => {
			return {
				hue: h(domainValue),
				saturation: s(domainValue),
				lightness: l(domainValue),
				alpha: a(domainValue)
			};
		},
		{
			domain: <[number, number]>h.domain,
			domainRange: h.domainRange,  //negative means start is higher then end
			domainValues: h.domainValues,
			hueTransfer: h,
			saturationTransfer: s,
			lightnessTransfer: l,
			alphaTransfer: a
		});
}

export const getCustomColorInterpolator = (colorPalette: ColorMapEntry[]): ColorInterpolator => {
	if (!colorPalette || colorPalette.length === 0) {
		throw "No color palette given.";
	}
	if (colorPalette.length === 1) {
		return getColorInterpolator(
			transferCharacteristic([0], [colorPalette[0].color.hue]),
			transferCharacteristic([0], [colorPalette[0].color.saturation]),
			transferCharacteristic([0], [colorPalette[0].color.lightness]),
			transferCharacteristic([0], [colorPalette[0].color.alpha])
		);
	} else {
		const palette: ColorMapEntry[] = [].concat(colorPalette).sort((entry0, entry1) => entry0.value - entry1.value);
		const domainSequence = palette.map(entry => entry.value);
		return getColorInterpolator(
			transferCharacteristic(domainSequence, palette.map(entry => entry.color.hue)),
			transferCharacteristic(domainSequence, palette.map(entry => entry.color.saturation)),
			transferCharacteristic(domainSequence, palette.map(entry => entry.color.lightness)),
			transferCharacteristic(domainSequence, palette.map(entry => entry.color.alpha))
		);
	}
}

/**
* return a color interpolator that maps values between 0 and 1 on a color comming from a mono hue scheme 
* based on given hue and optional alpha channel . 
 */
export const getMonoHueLinearInterpolator = (hue: number, alpha?: TransferCharacteristic): ColorInterpolator => {
	if (alpha && (alpha.domain[0] !== 0 || alpha.domain[1] !== 1)) {
		console.warn("Alpha channel given to construct a MonoHueLinearInterpolator is not defined on domain [0..1]. Alpha channel set to 1.");
	}
	return getColorInterpolator(
		transferCharacteristic(MONO_HUE_SATURATION_PROFILE.domain, [hue, hue]),
		MONO_HUE_SATURATION_PROFILE,
		MONO_HUE_LIGHTNESS_PROFILE,
		alpha && alpha.domain[0] === 0 && alpha.domain[1] === 1 ? alpha : undefined
	);
}
/**
* returns an interpolator that maps an arbitrary number to a color on  comming from a mono hue scheme
* based on given hue and optional alpha channel . The number is first run through an optional given domain 
* profile, otherwise the given number is used directly (these numbers must lie on the 0..1 interval) 
 */
export const getMonoHueInterpolator = (hue: number, alpha?: TransferCharacteristic, domainProfile?: TransferCharacteristic | [number, number]): ColorInterpolator => {
	const monoHueLinearInterpolator = getMonoHueLinearInterpolator(hue, alpha);
	if (domainProfile) {
		const dp = profile(domainProfile);
		return Object.assign(
			(domainValue: number) => monoHueLinearInterpolator(dp(domainValue)),
			{
				domain: dp.domain,
				domainRange: dp.domainRange,
				domainValues: dp.domainValues,
				hueTransfer: monoHueLinearInterpolator.hueTransfer,
				saturationTransfer: monoHueLinearInterpolator.saturationTransfer,
				lightnessTransfer: monoHueLinearInterpolator.lightnessTransfer,
				alphaTransfer: monoHueLinearInterpolator.alphaTransfer
			});
	} else {
		return monoHueLinearInterpolator;
	}
}

export const getColorPalette = (interpolator: ColorInterpolator, domain: number[] | number, count?: number): ColorMapEntry[] => {
	return <ColorMapEntry[]>createColorScheme(
		domainValue => { return { value: domainValue, color: interpolator(domainValue) } },
		interpolator, domain, count);
}

export const getColorScheme = (interpolator: ColorInterpolator, domain: number[] | number, count?: number): Color[] => {
	return <Color[]>createColorScheme(
		domainValue => interpolator(domainValue),
		interpolator, domain, count);
}

/**
* supported  calls:
* getColorScheme(interpolator, domain[]): return a color for each value in domain
* getColorScheme(interpolator, domain[], count):  take lowest and highest value in domain and take 'count' samples and return colors for each sample
* getColorScheme(interpolator, count):  take'count' samples from the iterators input domain and return colors for each sample
* @param interpolator the color interpolator where to extract the color scheme from
* @param domain the domain where to get color values for
*@param count ()optional) the number of colors to return  
 */
const createColorScheme = (output: (value: number) => any, interpolator: ColorInterpolator, domain: number[] | number, count?: number): any[] => {
	if ((<number[]>domain).push) {
		//domain represents an actual domain
		if ((<number[]>domain).length == 0) {
			throw "Given domain has no content ";
		}
		if (count == undefined) {
			//return a sample for each domain value
			return (<number[]>domain).map(output);
		} else {
			//return count samples on interval from lowest to highest value found in the domain
			const sortedDomain = [].concat(<number[]>domain).sort((v0, v1) => v0 - v1);
			return samples([sortedDomain[0], sortedDomain[sortedDomain.length - 1]], count).map(domainValue => interpolator(domainValue));
		}
	} else {
		//domain parameter actually  represents the count
		const cnt = <number>domain;
		return samples(interpolator.domain, cnt).map(domainValue => interpolator(domainValue));
	}
}

export const constraintColorInterpolator = (interpolator: ColorInterpolator, noAlpha = false, hideUnderflow = true, hideOverflow = false, normalizedInput = false): ColorInterpolator => {
	const domain = interpolator.domain;
	const domainRange = interpolator.domainRange;
	const isInc = domainRange > 0;
	const isUnderflow = isInc ? (value: number): boolean => value < domain[0] : (value: number): boolean => value > domain[0];
	const isOverflow = isInc ? (value: number): boolean => value > domain[1] : (value: number): boolean => value < domain[1];
	const alpha = noAlpha ? (_a: number) => 1.0 : (a: number) => a;
	const input = normalizedInput ? (t: number) => domain[0] + t * domainRange : (value: number) => value;

	const adjustedColorInterpolator: ColorInterpolator = Object.assign(
		(inputValue: number) => {
			const domainValue = input(inputValue);
			let color = (isUnderflow(inputValue) && hideUnderflow) || (isOverflow(inputValue) && hideOverflow) ? TRANSPARENT_COLOR : interpolator(domainValue);
			color.alpha = alpha(color.alpha);
			return color;
		},
		{
			domain: normalizedInput ? <[number, number]>[0, 1] : interpolator.domain,
			domainRange: normalizedInput ? 1 : interpolator.domainRange,
			domainValues: normalizedInput ? [0, 1] : interpolator.domainValues,
			hueTransfer: interpolator.hueTransfer,
			saturationTransfer: interpolator.saturationTransfer,
			lightnessTransfer: interpolator.lightnessTransfer,
			alphaTransfer: interpolator.alphaTransfer
		});
	return adjustedColorInterpolator;
};

export const normalizeColorInterpolator = (colorInterpolator: ColorInterpolator, subDomain?: [number, number]): ColorInterpolator => {
	return modifyDomain(colorInterpolator, [0, 1], subDomain);
}

export const modifyDomain = (colorInterpolator: ColorInterpolator, targetDomain: [number, number] = [0, 1], subDomain: [number, number] = [0, 1]): ColorInterpolator => {
	let sourceDomain = subDomain != undefined ? subDomain.map(t => (t * colorInterpolator.domainRange) + colorInterpolator.domain[0]) : colorInterpolator.domain;
	const targetToSource = Scaler({ from: targetDomain[0], to: targetDomain[1] }, { from: sourceDomain[0], to: sourceDomain[1] }, false);
	return Object.assign(
		(targetDomainValue: number) => colorInterpolator(targetToSource(targetDomainValue)),
		{
			domain: targetDomain,
			domainRange: targetDomain[1] - targetDomain[0],
			domainValues: targetDomain,
			hueTransfer: colorInterpolator.hueTransfer,
			saturationTransfer: colorInterpolator.saturationTransfer,
			lightnessTransfer: colorInterpolator.lightnessTransfer,
			alphaTransfer: colorInterpolator.alphaTransfer
		});
}

export const extractMonoHueInterpolator = (rawPalette: ColorMapEntry[], hue: number, rampStart = 0.3): ColorInterpolator => {
	const palette = [].concat(rawPalette).sort((entry0, entry1) => entry0.value - entry1.value);
	const iMax = palette.length - 1;

	//the first entry in the given palette is assumed to be domain value 0
	//the second entry in the given palette is assumed to be the lowest domain value of interrest
	//the last palette entry in the given palette is assumed to be the highest domain value of interrest

	const alphaUnderFlow = palette[0].color.alpha;
	const alphaStart = palette[1].color.alpha;
	const alphaEnd = palette[iMax].color.alpha;

	// the domain is the range of domain values that are considered of interest
	const domain: [number, number] = [palette[1].value, palette[iMax].value];
	//let the domain values only take up a portion of the available colors
	const domainProfile = transferCharacteristic([0, domain[0], domain[1]], [0.0, rampStart, 1.0]);
	//create the alpha channel for this palette (the linear version so apply the domainProfile to the domain values first)
	const alphaProfile = transferCharacteristic([domainProfile(0), domainProfile(domain[0]), domainProfile(domain[1])], [alphaUnderFlow, alphaStart, alphaEnd]);

	return getMonoHueInterpolator(hue, alphaProfile, domainProfile);
}

export const adjustColor = (color: Color, delta: Color): Color => {
	return {
		hue: color.hue + delta.hue,
		saturation: color.saturation + delta.saturation,
		lightness: color.lightness + delta.lightness,
		alpha: color.alpha + delta.alpha
	}
}

const createColorMap = (delta: Color) =>
	constraintColorInterpolator(getCustomColorInterpolator(paletteEntries.map(entry => {
		return {
			value: entry.value,
			color: adjustColor(entry.color, delta)
		};
	})));

const palettes = [
	createColorMap({ hue: 0.0, saturation: 0.0, lightness: 0.0, alpha: 0.0 }),
	createColorMap({ hue: 0.0, saturation: 0.0, lightness: -5.0, alpha: 0.0 }),
	createColorMap({ hue: 0.0, saturation: 0.0, lightness: -10.0, alpha: 0.0 }),
	createColorMap({ hue: 0.0, saturation: 0.0, lightness: -17.0, alpha: 0.0 }),
	createColorMap({ hue: 0.0, saturation: 0.0, lightness: -25.0, alpha: 0.0 })
];

export const getDefaultColorInterpolator = (paletteIndex: number): ColorInterpolator => {
	return palettes[paletteIndex];
}
export const getPaletteColor = (paletteIndex: number, hue: number): Color => {
	return getDefaultColorInterpolator(paletteIndex)(hue % 360);
}

const hueToRGB = (p: number, q: number, h: number): number => {
	if (h < 0) h += 1;

	if (h > 1) h -= 1;

	let v;
	if (6 * h < 1) {
		v = p + ((q - p) * 6 * h);
	} else if (2 * h < 1) {
		v = q;
	} else if (3 * h < 2) {
		v = p + ((q - p) * 6 * ((2.0 / 3.0) - h));
	} else {
		v = p;
	}

	return v < 0.0 ? 0.0 : v > 1.0 ? 1.0 : v;
}

export const hsla2rgba = (hsla: Color): [number, number, number, number] => {
	if (hsla.saturation > 0.0) {
		const h = (hsla.hue % 360.0) / 360.0;
		const q = hsla.lightness < 0.5 ? hsla.lightness * (1 + hsla.saturation) : (hsla.lightness + hsla.saturation) - (hsla.saturation * hsla.lightness);
		const p = 2 * hsla.lightness - q;
		return [Math.max(0, hueToRGB(p, q, h + (1.0 / 3.0))), Math.max(0, hueToRGB(p, q, h)), Math.max(0, hueToRGB(p, q, h - (1.0 / 3.0))), hsla.alpha];
	} else {
		return [hsla.lightness, hsla.lightness, hsla.lightness, hsla.alpha];
	}
}





const byte2Hex = (b: number): string => (b < 16 ? '0' : '') + b.toString(16);

export const rgb2css = (rgba: [number, number, number, number]): string => '#' +
	byte2Hex((rgba[0] * 255) & 0xFF) +
	byte2Hex((rgba[1] * 255) & 0xFF) +
	byte2Hex((rgba[2] * 255) & 0xFF) +
	byte2Hex((rgba[3] * 255) & 0xFF);

export const rgba2hsla = (rgba: [number, number, number, number]): Color => {

	const r = rgba[0];
	const g = rgba[1];
	const b = rgba[2];
	const a = rgba[3];

	//	Minimum and Maximum RGB values are used in the HSL calculations

	const min = Math.min(r, Math.min(g, b));
	const max = Math.max(r, Math.max(g, b));

	//  Calculate the Hue

	let h = 0;

	if (max == min)
		h = 0;
	else if (max == r)
		h = ((60 * (g - b) / (max - min)) + 360) % 360;
	else if (max == g)
		h = (60 * (b - r) / (max - min)) + 120;
	else if (max == b)
		h = (60 * (r - g) / (max - min)) + 240;

	//  Calculate the Lightness

	let l = (max + min) / 2;

	//  Calculate the Saturation

	let s = 0;

	if (max == min)
		s = 0;
	else if (l <= 0.5)
		s = (max - min) / (max + min);
	else
		s = (max - min) / (2 - max - min);

	return { hue: h, saturation: s, lightness: l, alpha: a };
}

